// ==========================================================
// Seguimiento de Pagos — expediente SIAF en el MEF
// ==========================================================
// El usuario digita año, unidad ejecutora y expediente, le aparece la imagen
// con el código del MEF, la digita y sale el expediente ordenado.
//
// La consulta sale por el puente que corre en el equipo (servidor-mef): el
// portal del MEF solo atiende conexiones domésticas peruanas —se comprobó que
// descarta las de Supabase, Vercel y Cloudflare— y además no manda cabeceras
// CORS ni suelta el captcha fuera de su cookie de sesión.
//
// Lo que aporta esta pantalla es la lectura: el MEF entrega una bitácora plana
// de movimientos y acá se ordena por la cadena del gasto —compromiso,
// devengado, girado, pagado— para responder en qué parte se quedó el pago. El
// detalle crudo sigue disponible en la última pestaña.
//
// La última consulta de cada expediente queda en localStorage (`nuvia:pagos:`)
// para abrirla sin repetir el trámite y avisar qué se movió desde la vez
// anterior.
//
// La pantalla la ve cualquier plan, pero cada consulta nueva gasta una del cupo
// del día que le corresponde a su plan (`cuotaPagos.js`): el puente es un solo
// equipo y su conexión es el permiso que el MEF nos da. Volver a un expediente
// ya consultado —con «Actualizar» o desde el historial— no gasta nada.

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Search, RefreshCw, AlertCircle, X, Download, Clock, ChevronRight,
  FileText, Landmark, Wallet, BadgeCheck, Receipt, History, Image as ImageIcon,
  PlugZap, Gauge, Lock
} from 'lucide-react';
import * as XLSX from 'xlsx';
import {
  pedirCaptcha,
  consultarExpediente,
  PUENTE_ES_LOCAL,
  leerHistorial,
  guardarEnHistorial,
  borrarDelHistorial,
  guardarSnapshot,
  leerSnapshot,
  compararConSnapshot,
  claveExpediente,
  formatearMonto,
  formatearPorcentaje,
  formatearFechaHora,
  nombreEstado,
  tonoEstado,
  FASES,
} from '../services/mefExpediente';
import { leerCuota, reservarConsulta, anularConsulta } from '../services/cuotaPagos';
import { limitePagosDia, LIMITES_PAGOS_DIA } from '../components/planPermissions';
import useAccesosPortal from '../hooks/useAccesosPortal';
import './seguimiento-pagos34.scss';

const ANIO_ACTUAL = String(new Date().getFullYear());

// Cupos de los planes de pago, para el texto que invita a subir de plan. Los
// números de verdad los pone Supabase (ver `cuotaPagos.js`).
const CUPO_PLUS = LIMITES_PAGOS_DIA.PREMIUM;
const CUPO_PREMIUM = LIMITES_PAGOS_DIA.PLUS_PLUS;

const soloDigitos = (valor, largo) => String(valor || '').replace(/\D/g, '').slice(0, largo);

/** Ancho de un tramo de barra, acotado para que nunca se desborde. */
const porcentaje = (parte, total) =>
  total > 0 ? Math.max(0, Math.min(100, (parte / total) * 100)) : 0;

// ----------------------------------------------------------
// Piezas sueltas
// ----------------------------------------------------------
const Chip = ({ tono = 'neutro', children }) => (
  <span className={`sp34-chip sp34-chip--${tono}`}>{children}</span>
);

const Kpi = ({ titulo, monto, pie, tono = 'neutro', destacado = false }) => (
  <div className={`sp34-kpi sp34-kpi--${tono} ${destacado ? 'is-destacado' : ''}`}>
    <span className="sp34-kpi__titulo">{titulo}</span>
    <strong className="sp34-kpi__monto">{monto}</strong>
    {pie && <span className="sp34-kpi__pie">{pie}</span>}
  </div>
);

/** Los cuatro eslabones del gasto, con el estado de cada uno. */
const Cadena = ({ cadena }) => (
  <div className="sp34-cadena">
    {cadena.map((paso, i) => (
      <React.Fragment key={paso.codigo}>
        <div className={`sp34-paso sp34-paso--${paso.estado}`}>
          <span className="sp34-paso__punto" aria-hidden="true" />
          <span className="sp34-paso__nombre">{paso.nombre}</span>
          <strong className="sp34-paso__monto">
            {paso.registros ? formatearMonto(paso.monto) : '—'}
          </strong>
          <span className="sp34-paso__pie">
            {paso.estado === 'pendiente'
              ? 'Sin registrar'
              : paso.estado === 'completo'
                ? `Aprobado · ${formatearFechaHora(paso.fecha)}`
                : `En trámite · ${formatearFechaHora(paso.fecha)}`}
          </span>
        </div>
        {i < cadena.length - 1 && <span className="sp34-cadena__flecha" aria-hidden="true" />}
      </React.Fragment>
    ))}
  </div>
);

/** Historial de estados por los que pasó un registro del SIAF. */
const HistorialFilas = ({ filas }) => (
  <table className="sp34-mini">
    <thead>
      <tr>
        <th>Paso</th>
        <th>Doc</th>
        <th>Número</th>
        <th className="num">Monto</th>
        <th>Estado</th>
        <th>Fecha de proceso</th>
      </tr>
    </thead>
    <tbody>
      {filas.map((f, i) => (
        <tr key={`${f.indice}-${i}`}>
          <td className="sp34-mini__paso">{i + 1}</td>
          <td>{f.codDoc || '—'}</td>
          <td className="sp34-mono">{f.numDoc || '—'}</td>
          <td className="num sp34-mono">
            {Number.isFinite(f.monto) ? formatearMonto(f.monto) : '—'}
          </td>
          <td>
            {f.estado ? (
              <Chip tono={tonoEstado(f.estado)}>{nombreEstado(f.estado)}</Chip>
            ) : (
              <span className="sp34-tenue">registro</span>
            )}
          </td>
          <td className="sp34-tenue">{formatearFechaHora(f.fechaProceso)}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

/** Tarjeta de un registro (compromiso, devengado o giro). */
const TarjetaRegistro = ({ registro, etiqueta }) => (
  <article className="sp34-registro">
    <header className="sp34-registro__head">
      <div className="sp34-registro__id">
        <span className={`sp34-registro__fase sp34-registro__fase--${FASES[registro.fase]?.clave}`}>
          {registro.fase}
        </span>
        <div>
          <strong>
            Secuencia {registro.secuencia} · Correlativo {registro.correlativo}
          </strong>
          <span className="sp34-registro__doc">
            {registro.documento || 'Sin documento'}
            {registro.numDoc ? ` N° ${registro.numDoc}` : ''}
            {registro.fechaDoc ? ` · ${registro.fechaDoc}` : ''}
          </span>
        </div>
      </div>
      <div className="sp34-registro__cifras">
        <strong>{Number.isFinite(registro.monto) ? formatearMonto(registro.monto) : '—'}</strong>
        <Chip tono={registro.tono}>{registro.estadoNombre}</Chip>
      </div>
    </header>

    {etiqueta && <p className="sp34-registro__nota">{etiqueta}</p>}

    <div className="sp34-registro__meta">
      {registro.ff && <span>Fuente de financiamiento {registro.ff}</span>}
      {registro.idTrx && <span>Id Trx {registro.idTrx}</span>}
      <span>{registro.filas.length} movimiento(s) registrados</span>
    </div>

    <HistorialFilas filas={registro.filas} />
  </article>
);

// ----------------------------------------------------------
// Cupo del día
// ----------------------------------------------------------
const consultas = (n) => (n === 1 ? '1 consulta' : `${n} consultas`);

/** Qué le queda al usuario hoy, para el chip de la cabecera. */
const textoCupo = ({ restantes, limite }) =>
  restantes > 0
    ? `Te ${restantes === 1 ? 'queda' : 'quedan'} ${restantes} de ${consultas(limite)} de hoy`
    : 'Cupo de hoy agotado';

/** La invitación a subir de plan, solo si hay un plan con más cupo que el suyo. */
const textoMejora = (limite) => {
  if (limite >= CUPO_PREMIUM) return null;
  const opciones = [
    limite < CUPO_PLUS && `el Plan Plus incluye ${CUPO_PLUS} al día`,
    `el Plan Premium, ${CUPO_PREMIUM}`,
  ].filter(Boolean);
  return `Si necesitas más, ${opciones.join(' y ')}.`;
};

/** Aviso del cupo agotado, en la cabecera del formulario. */
const CupoAgotado = ({ limite }) => {
  const mejora = textoMejora(limite);
  return (
    <div className="sp34-cupo">
      <Lock size={16} />
      <div>
        <strong>Usaste {consultas(limite)} de hoy</strong>
        <span>
          El cupo se renueva mañana a las 00:00 (hora de Perú).{mejora ? ` ${mejora}` : ''}
        </span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------
// Pantalla
// ----------------------------------------------------------
const SeguimientoPagosPage = ({ user }) => {
  const correo = user?.email || null;
  const { grupos: gruposCurso } = useAccesosPortal();

  const [form, setForm] = useState({
    anoEje: ANIO_ACTUAL,
    secEjec: '',
    expediente: '',
    captcha: '',
  });
  const [anios, setAnios] = useState([ANIO_ACTUAL]);
  // `fallo` guarda por qué no hay imagen: con 'puente' la pantalla explica
  // que el programita local está apagado, que es la causa más frecuente.
  const [captcha, setCaptcha] = useState({ imagen: null, sesion: null, fallo: null });
  const [cargandoCaptcha, setCargandoCaptcha] = useState(false);

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [novedades, setNovedades] = useState(null);
  const [tab, setTab] = useState('resumen');
  const [historial, setHistorial] = useState(leerHistorial);
  // Lo que dijo Supabase del cupo de hoy; null mientras no haya contestado.
  const [cuota, setCuota] = useState(null);

  const campoCaptcha = useRef(null);

  // --- Cupo del día ----------------------------------------------------
  // El cupo del plan se conoce sin preguntar (`limitePagosDia`), y así la
  // pantalla puede decir de una cuántas consultas incluye el plan. Lo que hace
  // falta preguntar es cuántas van usadas, y de eso manda la base.
  const limitePlan = useMemo(
    () => limitePagosDia(user?.plan, user?.curso_tipo, gruposCurso),
    [user?.plan, user?.curso_tipo, gruposCurso]
  );

  useEffect(() => {
    if (!correo) return undefined;
    let activo = true;
    leerCuota(correo).then((c) => {
      if (activo && c) setCuota(c);
    });
    return () => {
      activo = false;
    };
  }, [correo]);

  const ilimitado = cuota ? Boolean(cuota.ilimitado) : limitePlan === null;
  const limite = cuota?.limite ?? limitePlan;
  const restantes = ilimitado ? null : Math.max(0, cuota?.restantes ?? limite ?? 0);
  // Solo se bloquea con una respuesta de la base en la mano: si no contestó, la
  // reserva de más abajo es la que decide, y no se le cierra la pantalla a
  // nadie por un fallo de red.
  const sinCupo = !ilimitado && cuota != null && restantes <= 0;

  // --- Captcha ---------------------------------------------------------
  const cargarCaptcha = useCallback(async ({ foco = false } = {}) => {
    setCargandoCaptcha(true);
    setForm((f) => ({ ...f, captcha: '' }));
    try {
      const data = await pedirCaptcha();
      setCaptcha({ imagen: data.imagen, sesion: data.sesion, fallo: null });
      if (Array.isArray(data.anios) && data.anios.length) setAnios(data.anios);
      if (foco) campoCaptcha.current?.focus();
    } catch (e) {
      setCaptcha({ imagen: null, sesion: null, fallo: e.motivo || 'red' });
      setError({ texto: e.message, motivo: e.motivo });
    } finally {
      setCargandoCaptcha(false);
    }
  }, []);

  useEffect(() => {
    cargarCaptcha();
  }, [cargarCaptcha]);

  // --- Consulta --------------------------------------------------------
  const puedeConsultar =
    form.secEjec && form.expediente && form.captcha.length >= 4 && captcha.sesion &&
    !cargando && !sinCupo;

  const consultar = async (evento) => {
    evento?.preventDefault?.();
    if (!puedeConsultar) return;

    setCargando(true);
    setError(null);
    setNovedades(null);

    const consulta = {
      anoEje: form.anoEje,
      secEjec: form.secEjec,
      expediente: form.expediente,
    };

    // El cupo se reserva antes de salir al MEF y se devuelve en el `catch`: así
    // dos pestañas no se cuelan por el mismo hueco, y un código de imagen mal
    // digitado no le cuesta al usuario su consulta del día.
    const reserva = await reservarConsulta(correo, consulta);
    if (!reserva.permitido) {
      // Con el cupo agotado basta el aviso del formulario, que además dice
      // cuándo se renueva; un error rojo encima sería repetirlo.
      if (reserva.motivo === 'cuota') {
        setCuota(reserva);
      } else {
        setError({
          texto: 'No se pudo verificar tu plan. Vuelve a entrar al portal e inténtalo otra vez.',
          motivo: reserva.motivo,
        });
      }
      setCargando(false);
      return;
    }
    if (!reserva.sinContador) setCuota(reserva);

    try {
      const anterior = leerSnapshot(consulta);
      const resultado = await consultarExpediente({
        ...consulta,
        sesion: captcha.sesion,
        captcha: form.captcha,
      });

      setDatos(resultado);
      setTab('resumen');
      setNovedades(anterior ? compararConSnapshot(anterior, resultado) : null);
      guardarSnapshot(resultado);
      setHistorial(
        guardarEnHistorial({
          ...consulta,
          entidad: resultado.cabecera?.entidad || null,
        })
      );
    } catch (e) {
      setError({ texto: e.message, motivo: e.motivo });
      // La consulta no dio resultado: el cupo vuelve a su sitio.
      if (reserva.id) {
        await anularConsulta(correo, reserva.id);
        const fresca = await leerCuota(correo);
        if (fresca) setCuota(fresca);
      }
      // Si el portal renegó del código o la sesión venció, la imagen anterior
      // ya no sirve: se pide una nueva para poder reintentar de una.
      if (e.motivo === 'captcha' || e.motivo === 'sesion') {
        cargarCaptcha({ foco: true });
      }
    } finally {
      setCargando(false);
    }
  };

  /** Vuelve a pedir los datos reusando el captcha ya validado. */
  const actualizar = async () => {
    if (!datos?.sesion || cargando) return;
    setCargando(true);
    setError(null);
    try {
      const resultado = await consultarExpediente({
        ...datos.consulta,
        sesion: datos.sesion,
        captcha: datos.captcha,
        refrescar: true,
      });
      setNovedades(compararConSnapshot(datos, resultado));
      setDatos(resultado);
      guardarSnapshot(resultado);
    } catch (e) {
      setError({
        texto:
          e.motivo === 'captcha' || e.motivo === 'sesion'
            ? 'La sesión con el MEF venció. Vuelve a consultar con un código nuevo.'
            : e.message,
        motivo: e.motivo,
      });
      if (e.motivo === 'captcha' || e.motivo === 'sesion') cargarCaptcha();
    } finally {
      setCargando(false);
    }
  };

  /** Abre la última consulta guardada de un expediente del historial. */
  const abrirDelHistorial = (entrada) => {
    setForm((f) => ({ ...f, ...entrada, captcha: '' }));
    const guardado = leerSnapshot(entrada);
    setError(null);
    setNovedades(null);
    if (guardado) {
      setDatos(guardado);
      setTab('resumen');
    } else {
      setDatos(null);
      campoCaptcha.current?.focus();
    }
  };

  const nuevaConsulta = () => {
    setDatos(null);
    setError(null);
    setNovedades(null);
    setForm((f) => ({ ...f, secEjec: '', expediente: '', captcha: '' }));
    cargarCaptcha({ foco: true });
  };

  /** Vuelve al formulario con el mismo expediente para traer el estado de hoy. */
  const volverAConsultar = () => {
    setDatos(null);
    setError(null);
    setNovedades(null);
    cargarCaptcha({ foco: true });
  };

  // --- Excel -----------------------------------------------------------
  const exportar = () => {
    if (!datos) return;
    const filas = datos.movimientos.map((m) => ({
      Ciclo: m.ciclo,
      Fase: FASES[m.fase]?.nombre || m.fase,
      Secuencia: m.secuencia,
      Correlativo: m.correlativo,
      Doc: m.codDoc,
      Documento: m.documento,
      Número: m.numDoc,
      Fecha: m.fecha,
      FF: m.ff,
      Moneda: m.moneda,
      Monto: m.monto,
      Estado: m.estado ? nombreEstado(m.estado) : '',
      'Fecha de proceso': m.fechaProceso,
      'Id Trx': m.idTrx,
    }));

    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(filas), 'Movimientos');
    XLSX.writeFile(libro, `expediente-${claveExpediente(datos.consulta)}.xlsx`);
  };

  // --- Derivados -------------------------------------------------------
  const tabs = useMemo(() => {
    if (!datos) return [];
    return [
      { clave: 'resumen', nombre: 'Resumen' },
      { clave: 'compromisos', nombre: 'Compromisos', cuenta: datos.porFase.C.length },
      { clave: 'devengados', nombre: 'Devengados', cuenta: datos.porFase.D.length },
      { clave: 'girados', nombre: 'Girados', cuenta: datos.porFase.G.length },
      // La fase P no viene en todos los expedientes: la pestaña aparece solo
      // cuando el portal la publicó, para no dejar una sección siempre vacía.
      ...(datos.porFase.P.length
        ? [{ clave: 'pagados', nombre: 'Pagados', cuenta: datos.porFase.P.length }]
        : []),
      { clave: 'movimientos', nombre: 'Movimientos', cuenta: datos.movimientos.length },
    ];
  }, [datos]);

  // El portal cierra la bitácora en el girado y casi nunca publica la fase P.
  // Cuando no viene, el girado hace de cierre: es lo que el proveedor cobra.
  const hayFaseP = Boolean(datos?.porFase.P.length);

  // ----------------------------------------------------------
  return (
    <div className="sp34">
      <header className="sp34-head">
        <div>
          <h1>Seguimiento de Pagos</h1>
          <p>
            Consulta el expediente SIAF en el portal del MEF y revisa en qué parte de la
            cadena del gasto se quedó tu pago.
          </p>
        </div>
        <div className="sp34-head__acciones">
          {/* Cuántas consultas le quedan hoy. El administrador no tiene cupo,
              así que para él no hay nada que mostrar. */}
          {!ilimitado && limite != null && (
            <span className={`sp34-cupo-chip${sinCupo ? ' is-agotado' : ''}`}>
              <Gauge size={14} />
              {cuota ? textoCupo({ restantes, limite }) : `${consultas(limite)} al día`}
            </span>
          )}
          {datos && (
            <>
              {/* Sin sesión viva (consulta abierta desde el historial) no hay nada
                  que refrescar: toca digitar un código nuevo. */}
              {datos.sesion && (
                <button type="button" className="sp34-btn" onClick={actualizar} disabled={cargando}>
                  <RefreshCw size={15} className={cargando ? 'girando' : ''} />
                  Actualizar
                </button>
              )}
              <button type="button" className="sp34-btn" onClick={exportar}>
                <Download size={15} />
                Excel
              </button>
              <button type="button" className="sp34-btn sp34-btn--primario" onClick={nuevaConsulta}>
                <Search size={15} />
                Otra consulta
              </button>
            </>
          )}
        </div>
      </header>

      {/* ---------------- Formulario ---------------- */}
      {!datos && (
        <section className="sp34-panel sp34-consulta">
          {sinCupo && <CupoAgotado limite={limite} />}

          <form onSubmit={consultar}>
            <div className="sp34-consulta__campos">
              <label className="sp34-campo">
                <span>Año de ejecución</span>
                <select
                  value={form.anoEje}
                  onChange={(e) => setForm({ ...form, anoEje: e.target.value })}
                >
                  {anios.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </label>

              <label className="sp34-campo">
                <span>Código de Unidad Ejecutora</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="026"
                  value={form.secEjec}
                  onChange={(e) => setForm({ ...form, secEjec: soloDigitos(e.target.value, 6) })}
                />
              </label>

              <label className="sp34-campo">
                <span>Expediente SIAF</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="8184"
                  value={form.expediente}
                  onChange={(e) =>
                    setForm({ ...form, expediente: soloDigitos(e.target.value, 10) })
                  }
                />
              </label>
            </div>

            <div className="sp34-consulta__captcha">
              <div className="sp34-captcha">
                {captcha.imagen ? (
                  <img src={captcha.imagen} alt="Código de verificación del MEF" />
                ) : (
                  <div
                    className={`sp34-captcha__vacio${
                      captcha.fallo === 'puente' ? ' sp34-captcha__vacio--apagado' : ''
                    }`}
                  >
                    {cargandoCaptcha ? (
                      'Pidiendo el código al MEF…'
                    ) : captcha.fallo === 'puente' ? (
                      <>
                        <PlugZap size={16} />
                        {PUENTE_ES_LOCAL ? 'Puente apagado' : 'En mantenimiento'}
                      </>
                    ) : (
                      <>
                        <ImageIcon size={16} />
                        Sin imagen
                      </>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="sp34-captcha__recargar"
                  onClick={() => cargarCaptcha({ foco: true })}
                  disabled={cargandoCaptcha}
                >
                  <RefreshCw size={14} className={cargandoCaptcha ? 'girando' : ''} />
                  {captcha.imagen ? 'Cambiar imagen' : 'Reintentar'}
                </button>
              </div>

              <label className="sp34-campo sp34-campo--captcha">
                <span>Código de la imagen</span>
                <input
                  ref={campoCaptcha}
                  type="text"
                  value={form.captcha}
                  maxLength={5}
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder="5 caracteres"
                  onChange={(e) => setForm({ ...form, captcha: e.target.value.trim() })}
                />
              </label>

              <button
                type="submit"
                className="sp34-btn sp34-btn--primario"
                disabled={!puedeConsultar}
              >
                {cargando ? <RefreshCw size={15} className="girando" /> : <Search size={15} />}
                {cargando ? 'Consultando al MEF…' : 'Consultar'}
              </button>
            </div>

            <p className="sp34-consulta__ayuda">
              {captcha.fallo === 'puente' && PUENTE_ES_LOCAL ? (
                <>
                  No se pudo traer la imagen porque el puente está apagado. Enciéndelo con{' '}
                  <code>npm run mef</code> y pulsa «Reintentar»: la consulta tiene que salir de
                  esta conexión, el MEF no atiende a los servidores en la nube.
                </>
              ) : captcha.fallo === 'puente' ? (
                <>
                  La consulta está en mantenimiento momentáneo y no se pudo traer la imagen.
                  Vuelve a intentarlo en unos minutos con «Reintentar»; si sigue igual,
                  escríbenos.
                </>
              ) : (
                <>
                  El código lo pide el propio portal del MEF y cambia en cada consulta. Si no se
                  lee bien, usa «Cambiar imagen».
                </>
              )}
            </p>

            {/* Qué gasta cupo y qué no: solo la consulta nueva. Volver a un
                expediente ya consultado (desde el historial o con «Actualizar»)
                es gratis, para que nadie evite revisar su pago por miedo. */}
            {!ilimitado && limite != null && !sinCupo && (
              <p className="sp34-consulta__cupo">
                Tu plan incluye {consultas(limite)} al día. Actualizar un expediente que ya
                consultaste, o abrirlo desde «Consultas recientes», no usa cupo.
                {textoMejora(limite) ? ` ${textoMejora(limite)}` : ''}

              </p>
            )}
          </form>

          {historial.length > 0 && (
            <div className="sp34-historial">
              <span className="sp34-historial__titulo">
                <History size={14} />
                Consultas recientes
              </span>
              <div className="sp34-historial__lista">
                {historial.map((h) => (
                  <span key={claveExpediente(h)} className="sp34-historial__item">
                    <button type="button" onClick={() => abrirDelHistorial(h)}>
                      <strong>Exp. {h.expediente}</strong>
                      <span>
                        UE {h.secEjec} · {h.anoEje}
                        {h.entidad ? ` · ${h.entidad}` : ''}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="sp34-historial__quitar"
                      aria-label="Quitar del historial"
                      onClick={() => setHistorial(borrarDelHistorial(h))}
                    >
                      <X size={13} />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ---------------- Errores ---------------- */}
      {error && (
        <div className="sp34-error">
          <AlertCircle size={16} />
          <p>
            {error.texto}
            {/* El detalle del puente solo sirve en desarrollo, donde quien lee
                es quien lo enciende; al usuario del portal se le dice que el
                servicio está en mantenimiento y nada más. */}
            {error.motivo === 'puente' && PUENTE_ES_LOCAL && (
              <>
                {' '}
                El puente es el programita que hace la consulta desde esta conexión, porque el
                MEF no atiende a los servidores en la nube.
              </>
            )}
          </p>
          <button type="button" onClick={() => setError(null)} aria-label="Cerrar aviso">
            <X size={14} />
          </button>
        </div>
      )}

      {/* ---------------- Resultado ---------------- */}
      {datos && (
        <>
          <section className="sp34-ficha">
            <div className="sp34-ficha__id">
              <Landmark size={18} />
              <div>
                <h2>{datos.cabecera.entidad || `Unidad ejecutora ${datos.consulta.secEjec}`}</h2>
                <span>
                  Expediente {datos.cabecera.expediente || datos.consulta.expediente} ·
                  Año {datos.cabecera.anoEje || datos.consulta.anoEje} ·
                  UE {datos.cabecera.secEjec || datos.consulta.secEjec}
                </span>
              </div>
            </div>

            <div className={`sp34-situacion sp34-situacion--${datos.situacion.tono}`}>
              <strong>{datos.situacion.titulo}</strong>
              <span>{datos.situacion.detalle}</span>
            </div>
          </section>

          <section className="sp34-kpis">
            <Kpi
              titulo="Comprometido"
              monto={formatearMonto(datos.totales.comprometido)}
              pie={`${datos.porFase.C.length} secuencia(s)`}
              tono="azul"
            />
            <Kpi
              titulo="Devengado"
              monto={formatearMonto(datos.totales.devengado)}
              pie={`Saldo por devengar ${formatearMonto(datos.totales.saldoPorDevengar)}`}
              tono="ambar"
            />
            <Kpi
              // El monto firme es el aprobado: un giro en verificación o en
              // firma todavía se puede caer, y es lo que el proveedor cobra.
              titulo="Girado aprobado"
              monto={formatearMonto(datos.totales.giradoAprobado)}
              pie={
                datos.totales.giradoEnTramite > 0
                  ? `${formatearMonto(datos.totales.giradoEnTramite)} más en trámite en Tesorería`
                  : hayFaseP
                    ? 'Todo el giro está aprobado'
                    : 'Todo el giro está aprobado · el abono lo hace el banco'
              }
              // Sin fase P el girado es el cierre de la cadena: se destaca, y
              // va en verde solo cuando ya no queda nada en trámite.
              tono={!hayFaseP && datos.totales.giradoEnTramite === 0 ? 'verde' : 'teal'}
              destacado={!hayFaseP}
            />
            {hayFaseP && (
              <Kpi
                titulo="Pagado"
                monto={formatearMonto(datos.totales.pagado)}
                pie={
                  datos.totales.porGirar > 0
                    ? `Por girar ${formatearMonto(datos.totales.porGirar)}`
                    : 'Sin saldo pendiente de giro'
                }
                tono="verde"
                destacado
              />
            )}
            <Kpi
              titulo="Avance"
              monto={formatearPorcentaje(datos.totales.avance)}
              pie="Girado aprobado sobre lo comprometido"
              tono="neutro"
            />
          </section>

          <Cadena cadena={datos.cadena} />

          {novedades?.hay && (
            <div className="sp34-aviso sp34-aviso--nuevo">
              <BadgeCheck size={16} />
              <p>{novedades.nuevos} movimiento(s) nuevos desde tu consulta anterior.</p>
            </div>
          )}

          {datos.desdeCache && (
            <div className="sp34-aviso">
              <Clock size={16} />
              <p>
                Estás viendo la consulta guardada del{' '}
                {new Date(datos.consultadoEn).toLocaleString('es-PE')}.
              </p>
              <button type="button" className="sp34-btn sp34-btn--claro" onClick={volverAConsultar}>
                Traer el estado de hoy
              </button>
            </div>
          )}

          {datos.truncado && (
            <div className="sp34-aviso sp34-aviso--alerta">
              <AlertCircle size={16} />
              <p>
                El expediente tiene más páginas de las que se pudieron leer: el detalle puede
                estar incompleto.
              </p>
            </div>
          )}

          <nav className="sp34-tabs">
            {tabs.map((t) => (
              <button
                key={t.clave}
                type="button"
                className={tab === t.clave ? 'is-activo' : ''}
                onClick={() => setTab(t.clave)}
              >
                {t.nombre}
                {typeof t.cuenta === 'number' && <em>{t.cuenta}</em>}
              </button>
            ))}
          </nav>

          {/* --------- Resumen --------- */}
          {tab === 'resumen' && (
            <div className="sp34-grid">
              <section className="sp34-panel">
                <h3>
                  <FileText size={16} />
                  Datos del expediente
                </h3>
                <dl className="sp34-datos">
                  <div>
                    <dt>Tipo de operación</dt>
                    <dd>{datos.cabecera.tipoOperacionNombre || '—'}</dd>
                  </div>
                  <div>
                    <dt>Modalidad de compra</dt>
                    <dd>{datos.cabecera.modalidadCompraNombre || '—'}</dd>
                  </div>
                  <div>
                    <dt>Tipo de proceso</dt>
                    <dd>{datos.cabecera.tipoProcesoNombre || 'No consignado'}</dd>
                  </div>
                  <div>
                    <dt>Fuente de financiamiento</dt>
                    <dd>{datos.cabecera.fuenteFinanciamiento || '—'}</dd>
                  </div>
                  <div>
                    <dt>Movimientos registrados</dt>
                    <dd>{datos.movimientos.length}</dd>
                  </div>
                  <div>
                    <dt>Última consulta</dt>
                    <dd>{new Date(datos.consultadoEn).toLocaleString('es-PE')}</dd>
                  </div>
                </dl>
              </section>

              <section className="sp34-panel">
                <h3>
                  <Wallet size={16} />
                  Pagos identificados
                  <span className="sp34-panel__cuenta">{datos.pagos.length}</span>
                </h3>

                {datos.pagos.length === 0 && (
                  <p className="sp34-vacio">
                    Todavía no hay devengados: el expediente solo tiene el compromiso
                    registrado.
                  </p>
                )}

                {datos.pagos.map((pago) => (
                  <article key={pago.numero} className="sp34-pago">
                    <header>
                      <div>
                        <span className="sp34-pago__n">Pago {pago.numero}</span>
                        <strong>{formatearMonto(pago.monto)}</strong>
                        <span className="sp34-pago__doc">
                          {pago.devengado.documento || 'Documento no consignado'}
                          {pago.devengado.numDoc ? ` N° ${pago.devengado.numDoc}` : ''}
                        </span>
                      </div>
                      <Chip tono={pago.estado.tono}>{pago.estado.nombre}</Chip>
                    </header>

                    <div className="sp34-pago__barras">
                      <span>Devengado</span>
                      <div className="sp34-barra">
                        <i style={{ width: '100%' }} className="sp34-barra--devengado" />
                      </div>
                      <strong>{formatearMonto(pago.monto)}</strong>

                      <span>Girado</span>
                      {/* Dos tramos: lo aprobado en firme y, rayado, lo que
                          sigue en Tesorería esperando firma o verificación. */}
                      <div className="sp34-barra">
                        <i
                          className="sp34-barra--girado"
                          style={{ width: `${porcentaje(pago.giradoAprobado, pago.monto)}%` }}
                        />
                        <i
                          className="sp34-barra--tramite"
                          style={{
                            width: `${porcentaje(
                              pago.giradoTotal - pago.giradoAprobado,
                              pago.monto
                            )}%`,
                          }}
                        />
                      </div>
                      <strong>
                        {formatearMonto(pago.giradoAprobado)}
                        {pago.giradoTotal > pago.giradoAprobado && (
                          <em className="sp34-pago__tramite">
                            +{formatearMonto(pago.giradoTotal - pago.giradoAprobado)} en trámite
                          </em>
                        )}
                      </strong>
                    </div>

                    {pago.detraccion && (
                      <div className="sp34-detraccion">
                        <Receipt size={15} />
                        <div>
                          <strong>Detracción SUNAT {pago.detraccion.tasa}%</strong>
                          <span>
                            {formatearMonto(pago.detraccion.monto)} van a la cuenta de
                            detracciones del Banco de la Nación
                            {pago.detraccion.cuenta ? ` (NP ${pago.detraccion.cuenta})` : ''}.
                            Al proveedor le llegan {formatearMonto(pago.montoLibre)}.
                          </span>
                        </div>
                      </div>
                    )}

                    {pago.giros.length > 0 && (
                      <ul className="sp34-pago__giros">
                        {pago.giros.map((g) => (
                          <li key={g.clave}>
                            <ChevronRight size={13} />
                            <span className="sp34-mono">
                              Sec {g.secuencia} · {g.numDoc || 'sin nota de pago'}
                            </span>
                            <strong>{formatearMonto(g.monto)}</strong>
                            <Chip tono={g.tono}>{g.estadoNombre}</Chip>
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}

                {datos.girosSinVincular.length > 0 && (
                  <p className="sp34-vacio">
                    {datos.girosSinVincular.length} giro(s) no calzan con ningún devengado;
                    están en la pestaña Girados.
                  </p>
                )}
              </section>
            </div>
          )}

          {/* --------- Fases --------- */}
          {tab === 'compromisos' && (
            <section className="sp34-lista">
              {datos.porFase.C.length === 0 && (
                <p className="sp34-vacio">El expediente no tiene compromisos registrados.</p>
              )}
              {datos.porFase.C.map((r) => (
                <TarjetaRegistro
                  key={r.clave}
                  registro={r}
                  etiqueta="El compromiso reserva el presupuesto del contrato u orden."
                />
              ))}
            </section>
          )}

          {tab === 'devengados' && (
            <section className="sp34-lista">
              {datos.porFase.D.length === 0 && (
                <p className="sp34-vacio">
                  Aún no hay devengados: falta que la entidad registre la conformidad.
                </p>
              )}
              {datos.porFase.D.map((r) => (
                <TarjetaRegistro
                  key={r.clave}
                  registro={r}
                  etiqueta="El devengado es la conformidad: reconoce la obligación de pagar."
                />
              ))}
            </section>
          )}

          {tab === 'girados' && (
            <section className="sp34-lista">
              {datos.porFase.G.length === 0 && (
                <p className="sp34-vacio">Todavía no hay giros registrados en Tesorería.</p>
              )}
              {datos.porFase.G.map((r) => (
                <TarjetaRegistro
                  key={r.clave}
                  registro={r}
                  etiqueta="El girado es la orden de pago que Tesorería manda al banco."
                />
              ))}
            </section>
          )}

          {tab === 'pagados' && (
            <section className="sp34-lista">
              {datos.porFase.P.map((r) => (
                <TarjetaRegistro
                  key={r.clave}
                  registro={r}
                  etiqueta="El pagado confirma que el dinero salió de la cuenta del Tesoro."
                />
              ))}
            </section>
          )}

          {/* --------- Detalle crudo --------- */}
          {tab === 'movimientos' && (
            <section className="sp34-panel">
              <h3>
                <FileText size={16} />
                Movimientos tal como los publica el MEF
              </h3>
              <div className="sp34-tabla-scroll">
                <table className="sp34-tabla">
                  <thead>
                    <tr>
                      <th>Ciclo</th>
                      <th>Fase</th>
                      <th>Sec</th>
                      <th>Corr</th>
                      <th>Doc</th>
                      <th>Número</th>
                      <th>Fecha</th>
                      <th>FF</th>
                      <th className="num">Monto</th>
                      <th>Estado</th>
                      <th>Fecha de proceso</th>
                      <th>Id Trx</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.movimientos.map((m, i) => (
                      <tr key={i}>
                        <td>{m.ciclo || '—'}</td>
                        <td>
                          <span className={`sp34-fase sp34-fase--${FASES[m.fase]?.clave || 'otro'}`}>
                            {m.fase || '—'}
                          </span>
                        </td>
                        <td>{m.secuencia || '—'}</td>
                        <td>{m.correlativo || '—'}</td>
                        <td title={m.documento || ''}>{m.codDoc || '—'}</td>
                        <td className="sp34-mono">{m.numDoc || '—'}</td>
                        <td>{m.fecha || '—'}</td>
                        <td>{m.ff || '—'}</td>
                        <td className="num sp34-mono">
                          {Number.isFinite(m.monto)
                            ? formatearMonto(m.monto, (m.moneda || 'S/').replace(/\.$/, ''))
                            : '—'}
                        </td>
                        <td>
                          {m.estado ? (
                            <Chip tono={tonoEstado(m.estado)}>{nombreEstado(m.estado)}</Chip>
                          ) : (
                            <span className="sp34-tenue">—</span>
                          )}
                        </td>
                        <td className="sp34-tenue">{m.fechaProceso || '—'}</td>
                        <td className="sp34-mono sp34-tenue">{m.idTrx || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default SeguimientoPagosPage;
